/**
 * SEO primitives for the Corgi Funds SPA.
 *
 * This is a client-rendered Vite SPA served from S3/CloudFront with a single
 * static `index.html`, so without help every route shares one `<title>` and
 * description. These primitives give each route its own crawl-relevant head:
 *
 *   • `<Seo>`      — imperatively manages `<title>`, description, canonical,
 *                    robots, Open Graph, and Twitter Card tags for the active
 *                    route. Exactly ONE `<Seo>` should render per page.
 *   • `<JsonLd>`   — emits a `application/ld+json` structured-data block.
 *                    Safe to render many per page (sitewide + page-specific).
 *   • builders     — typed helpers that produce schema.org JSON-LD objects
 *                    (Organization, WebSite, BreadcrumbList, FinancialProduct,
 *                    BlogPosting, CollectionPage).
 *
 * Google executes JS and renders the SPA, so runtime-injected head + JSON-LD
 * are indexed. The static `index.html` carries strong defaults for the
 * JS-less social scrapers (Slack/Discord/Facebook/LinkedIn) that only read the
 * first server response.
 */
import { type ReactElement, useEffect } from "react";

/** Single source of truth for absolute-URL construction and brand defaults. */
export const SITE = {
  /** Canonical production origin — no trailing slash. */
  origin: "https://corgifunds.com",
  name: "Corgi Funds",
  defaultTitle: "Corgi Funds — ETFs for the Future",
  defaultDescription:
    "ETFs designed to offer direct access to disruptive themes and companies shaping the future.",
  /** Absolute default share image (1200×630-ish hero). webp is accepted by all major scrapers. */
  defaultImage: "https://corgifunds.com/images/etf/landing-hero-bg.webp",
  twitterHandle: "@corgifunds",
  locale: "en_US",
} as const;

/** Resolve a path or absolute URL to an absolute URL on the canonical origin. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE.origin}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export interface SeoProps {
  /** Page title. The site name is appended automatically unless `bareTitle`. */
  title?: string;
  /** Render `title` verbatim without the " — Corgi Funds" suffix. */
  bareTitle?: boolean;
  description?: string;
  /** Path (preferred) or absolute URL for the canonical + og:url. */
  canonicalPath?: string;
  /** Share image — path or absolute URL. Falls back to the site default. */
  image?: string;
  /** Open Graph type. Defaults to "website"; use "article" for blog posts. */
  type?: "website" | "article" | "product";
  /** When true, emit `noindex,nofollow` (404s, thin/utility pages). */
  noindex?: boolean;
  /** Optional comma-joined keywords. */
  keywords?: string;
}

type TagSpec =
  | { kind: "meta"; key: string; attr: "name" | "property"; content: string }
  | { kind: "link"; key: string; rel: string; href: string };

const MANAGED = "data-seo";

/**
 * Reconcile the document head to match `specs`, replacing any tags this hook
 * previously created. Idempotent: re-running with the same specs is a no-op
 * beyond attribute writes.
 */
function reconcileHead(title: string, specs: TagSpec[]) {
  document.title = title;

  const head = document.head;
  const existing = new Map<string, Element>();
  for (const el of head.querySelectorAll(`[${MANAGED}]`)) {
    const key = el.getAttribute(MANAGED);
    if (key) existing.set(key, el);
  }

  const seen = new Set<string>();
  for (const spec of specs) {
    seen.add(spec.key);
    let el = existing.get(spec.key);
    if (spec.kind === "meta") {
      if (!el || el.tagName !== "META") {
        el?.remove();
        el = document.createElement("meta");
        el.setAttribute(MANAGED, spec.key);
        head.appendChild(el);
      }
      el.setAttribute(spec.attr, spec.key.replace(/^meta:/, ""));
      el.setAttribute("content", spec.content);
    } else {
      if (!el || el.tagName !== "LINK") {
        el?.remove();
        el = document.createElement("link");
        el.setAttribute(MANAGED, spec.key);
        head.appendChild(el);
      }
      el.setAttribute("rel", spec.rel);
      el.setAttribute("href", spec.href);
    }
  }

  // Drop tags from a previous route that the current one no longer declares.
  for (const [key, el] of existing) {
    if (!seen.has(key)) el.remove();
  }
}

/**
 * Manage the document head for the active route. Renders nothing.
 *
 * Use exactly one `<Seo>` per page so the route owns a single coherent set of
 * head tags; mounting two races them through the shared reconcile pass.
 */
export function Seo(props: SeoProps): null {
  const {
    title,
    bareTitle,
    description = SITE.defaultDescription,
    canonicalPath,
    image,
    type = "website",
    noindex = false,
    keywords,
  } = props;

  const fullTitle = !title
    ? SITE.defaultTitle
    : bareTitle || title.includes(SITE.name)
      ? title
      : `${title} — ${SITE.name}`;

  const url = canonicalPath ? absoluteUrl(canonicalPath) : SITE.origin;
  const img = absoluteUrl(image ?? SITE.defaultImage);

  useEffect(() => {
    const meta = (key: string, attr: "name" | "property", content: string): TagSpec => ({
      kind: "meta",
      key: `meta:${key}`,
      attr,
      content,
    });

    const specs: TagSpec[] = [
      meta("description", "name", description),
      meta("robots", "name", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"),
      // Open Graph
      meta("og:type", "property", type),
      meta("og:site_name", "property", SITE.name),
      meta("og:title", "property", fullTitle),
      meta("og:description", "property", description),
      meta("og:url", "property", url),
      meta("og:image", "property", img),
      meta("og:locale", "property", SITE.locale),
      // Twitter
      meta("twitter:card", "name", "summary_large_image"),
      meta("twitter:site", "name", SITE.twitterHandle),
      meta("twitter:title", "name", fullTitle),
      meta("twitter:description", "name", description),
      meta("twitter:image", "name", img),
      // Canonical
      { kind: "link", key: "link:canonical", rel: "canonical", href: url },
    ];

    if (keywords) specs.push(meta("keywords", "name", keywords));

    reconcileHead(fullTitle, specs);
  }, [fullTitle, description, url, img, type, noindex, keywords]);

  return null;
}

/**
 * Emit a JSON-LD structured-data block. Rendered inline (Google reads
 * `application/ld+json` anywhere in the document) so it is prerender-safe and
 * needs no head bookkeeping. Render as many as a page needs.
 */
export function JsonLd({ data }: { data: object | object[] }): ReactElement {
  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: serialized, non-executable LD+JSON.
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString(data) }} />
  );
}

/** Serialize JSON-LD, escaping `<` so a `</script>` in any string can't break out. */
function jsonLdString(data: object | object[]): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

// ─── Structured-data builders ────────────────────────────────────────────────

/** The publisher/provider entity — referenced by funds, posts, and the site. */
export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE.origin}/#organization`,
    name: SITE.name,
    url: SITE.origin,
    logo: absoluteUrl("/images/etf/corgifunds-logo-orange.webp"),
    description: SITE.defaultDescription,
    sameAs: ["https://blog.corgifunds.com"],
  };
}

/** Site-level entity enabling the Google sitelinks search box. */
export function webSiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.origin}/#website`,
    name: SITE.name,
    url: SITE.origin,
    publisher: { "@id": `${SITE.origin}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE.origin}/products?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** A breadcrumb trail. `items` are ordered root→current; the last may omit a path. */
export function breadcrumbLd(items: Array<{ name: string; path?: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      ...(item.path ? { item: absoluteUrl(item.path) } : {}),
    })),
  };
}

interface FundLd {
  ticker: string;
  name: string;
  fullName?: string | null;
  description?: string | null;
  summary?: string | null;
  exchange?: string | null;
  isin?: string | null;
  category?: string;
  slug: string;
  image?: string;
}

/** schema.org FinancialProduct for a fund detail page. */
export function financialProductLd(fund: FundLd) {
  const description = fund.summary ?? fund.description ?? SITE.defaultDescription;
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "@id": `${SITE.origin}/${fund.slug}#product`,
    name: fund.fullName ?? fund.name,
    alternateName: fund.ticker,
    tickerSymbol: fund.ticker,
    category: fund.category ?? "Exchange-Traded Fund",
    description,
    url: absoluteUrl(`/${fund.slug}`),
    ...(fund.image ? { image: absoluteUrl(fund.image) } : {}),
    ...(fund.isin
      ? { identifier: { "@type": "PropertyValue", name: "ISIN", value: fund.isin } }
      : {}),
    provider: { "@id": `${SITE.origin}/#organization` },
    ...(fund.exchange
      ? {
          offers: {
            "@type": "Offer",
            availableAtOrFrom: { "@type": "Place", name: fund.exchange },
          },
        }
      : {}),
  };
}

interface PostLd {
  slug: string;
  title: string;
  excerpt?: string;
  author?: string;
  category?: string;
  publishedAt?: string | null;
  image?: string;
}

/** schema.org BlogPosting for a single blog article. */
export function blogPostingLd(post: PostLd) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${SITE.origin}/blog/${post.slug}#post`,
    headline: post.title,
    description: post.excerpt ?? "",
    url: absoluteUrl(`/blog/${post.slug}`),
    image: absoluteUrl(post.image ?? SITE.defaultImage),
    ...(post.publishedAt
      ? { datePublished: post.publishedAt, dateModified: post.publishedAt }
      : {}),
    ...(post.category ? { articleSection: post.category } : {}),
    author: { "@type": "Organization", name: post.author ?? SITE.name },
    publisher: { "@id": `${SITE.origin}/#organization` },
    mainEntityOfPage: absoluteUrl(`/blog/${post.slug}`),
  };
}

/** schema.org CollectionPage with an embedded ItemList of links. */
export function collectionPageLd(opts: {
  name: string;
  description: string;
  path: string;
  items: Array<{ name: string; path: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.path),
    isPartOf: { "@id": `${SITE.origin}/#website` },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: opts.items.length,
      itemListElement: opts.items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        url: absoluteUrl(item.path),
      })),
    },
  };
}
